import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../context/AuthContext";
import { API, authFetch } from "../App";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import PortalLayout from "../components/PortalLayout";
import {
  Users,
  UserPlus,
  User,
  IndianRupee,
  Clock,
  CheckCircle,
  FileText,
  BookOpen,
  Settings,
  Mail,
  Search,
  MessageSquare,
  Eye,
  ChevronRight,
  TrendingUp,
  Shield,
  AlertCircle,
  Plus,
  X,
  Trash2,
  Menu,
  LayoutDashboard,
  UserCheck,
  Tag,
  Percent,
  ArrowUpRight,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [pendingClients, setPendingClients] = useState([]);
  const [allClients, setAllClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [inquiries, setInquiries] = useState([]);
  const [teamPerformance, setTeamPerformance] = useState([]);
  const [allThreads, setAllThreads] = useState([]);
  const [threadMessages, setThreadMessages] = useState([]);
  const [viewingThread, setViewingThread] = useState(null);

  // Allocation
  const [selectedClient, setSelectedClient] = useState(null);
  const [selectedTeamMember, setSelectedTeamMember] = useState("");
  const [allocating, setAllocating] = useState(false);

  // Role edit
  const [roleDialogOpen, setRoleDialogOpen] = useState(false);
  const [selectedUserForRole, setSelectedUserForRole] = useState(null);
  const [newRole, setNewRole] = useState("");

  // Create user
  const [createUserOpen, setCreateUserOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ name: "", email: "", password: "", role: "client" });
  const [creating, setCreating] = useState(false);

  // Search & filters
  const [clientSearch, setClientSearch] = useState("");
  const [userSearch, setUserSearch] = useState("");
  const [filterTeamMemberId, setFilterTeamMemberId] = useState(null);
  const { tab } = useParams();
  const activeAdminTab = tab || "analytics";

  // Services & Pricing
  const [services, setServices] = useState([]);
  const [complexityRules, setComplexityRules] = useState([]);
  const [editingService, setEditingService] = useState(null);
  const [editingRule, setEditingRule] = useState(null);
  const [newServiceOpen, setNewServiceOpen] = useState(false);
  const [newServiceForm, setNewServiceForm] = useState({ name: "", category: "addon", description: "", base_price: "" });
  const [newRuleOpen, setNewRuleOpen] = useState(false);
  const [newRuleForm, setNewRuleForm] = useState({ rule_type: "addon", condition_key: "", label: "", amount: "" });
  const [savingService, setSavingService] = useState(false);

  // Delete/Moderation
  const [deleteUserOpen, setDeleteUserOpen] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [deleteUserName, setDeleteUserName] = useState("");
  const [deleteThreadOpen, setDeleteThreadOpen] = useState(false);
  const [deleteThreadId, setDeleteThreadId] = useState(null);
  const [deleteInquiryOpen, setDeleteInquiryOpen] = useState(false);
  const [deleteInquiryId, setDeleteInquiryId] = useState(null);

  // Discount Codes
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newDiscountOpen, setNewDiscountOpen] = useState(false);
  const [newDiscountForm, setNewDiscountForm] = useState({ code: "", description: "", discount_type: "percentage", discount_value: "", max_uses: "", min_amount: "", expires_at: "" });

  // Custom Rates
  const [customRates, setCustomRates] = useState([]);
  const [newRateOpen, setNewRateOpen] = useState(false);
  const [newRateForm, setNewRateForm] = useState({ user_id: "", service_category: "itr_filing", custom_price: "", notes: "" });

  // Track which tabs have been loaded (ref to avoid useEffect dep issues)
  const tabLoadedRef = useRef({});
  const [tabLoading, setTabLoading] = useState({});
  const [refreshKey, setRefreshKey] = useState(0);

  // Simple refetch trigger for mutation handlers
  const fetchData = () => {
    tabLoadedRef.current = {};
    setRefreshKey((k) => k + 1);
  };

  // Fetch data for the active tab
  useEffect(() => {
    let cancelled = false;
    const fetchActiveTab = async () => {
      const tab = activeAdminTab;
      if (tabLoadedRef.current[tab] && refreshKey === 0) return;
      setTabLoading((prev) => ({ ...prev, [tab]: true }));
      try {
        switch (tab) {
          case "analytics": {
            const res = await authFetch(`${API}/admin/analytics`);
            if (!cancelled && res.ok) setAnalytics(await res.json());
            break;
          }
          case "allocation": {
            const [pendingRes, teamRes] = await Promise.all([
              authFetch(`${API}/admin/pending-allocation`),
              authFetch(`${API}/admin/team-members`),
            ]);
            if (!cancelled) {
              if (pendingRes.ok) setPendingClients(await pendingRes.json());
              if (teamRes.ok) setTeamMembers(await teamRes.json());
            }
            break;
          }
          case "clients": {
            const res = await authFetch(`${API}/admin/all-clients`);
            if (!cancelled && res.ok) setAllClients(await res.json());
            break;
          }
          case "team": {
            const res = await authFetch(`${API}/admin/team-performance`);
            if (!cancelled && res.ok) setTeamPerformance(await res.json());
            break;
          }
          case "users": {
            const res = await authFetch(`${API}/admin/users`);
            if (!cancelled && res.ok) {
              const users = await res.json();
              for (const u of users) u.is_first_login = !!u.is_first_login;
              setAllUsers(users);
            }
            break;
          }
          case "messages": {
            const res = await authFetch(`${API}/admin/all-threads-enriched`);
            if (!cancelled && res.ok) setAllThreads(await res.json());
            break;
          }
          case "inquiries": {
            const res = await authFetch(`${API}/admin/inquiries`);
            if (!cancelled && res.ok) setInquiries(await res.json());
            break;
          }
          case "services": {
            const res = await authFetch(`${API}/admin/services`);
            if (!cancelled && res.ok) {
              const data = await res.json();
              setServices(data.services || []);
              setComplexityRules(data.rules || []);
            }
            break;
          }
          case "discounts": {
            const res = await authFetch(`${API}/admin/discount-codes`);
            if (!cancelled && res.ok) setDiscountCodes(await res.json());
            break;
          }
          case "custom-rates": {
            const [crRes, clientsRes] = await Promise.all([
              authFetch(`${API}/admin/custom-rates`),
              authFetch(`${API}/admin/all-clients`),
            ]);
            if (!cancelled) {
              if (crRes.ok) setCustomRates(await crRes.json());
              if (clientsRes?.ok) setAllClients(await clientsRes.json());
            }
            break;
          }
          default: break;
        }
      } catch (error) {
        console.error(`Error fetching ${tab} data:`, error);
      } finally {
        if (!cancelled) {
          tabLoadedRef.current = { ...tabLoadedRef.current, [tab]: true };
          setTabLoading((prev) => ({ ...prev, [tab]: false }));
          setLoading(false);
        }
      }
    };
    fetchActiveTab();
    return () => { cancelled = true; };
  }, [activeAdminTab, refreshKey]);

  const TabSpinner = () => (
    <div className="flex items-center justify-center py-16">
      <div className="spinner"></div>
    </div>
  );

  const handleAllocateClient = async () => {
    if (!selectedClient || !selectedTeamMember) { toast.error("Select both client and team member"); return; }
    setAllocating(true);
    try {
      const res = await authFetch(`${API}/admin/allocate-client`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedClient, team_member_id: selectedTeamMember }),
      });
      if (res.ok) { toast.success("Client allocated"); setSelectedClient(null); setSelectedTeamMember(""); fetchData(); }
      else toast.error("Failed to allocate");
    } catch { toast.error("Error"); } finally { setAllocating(false); }
  };

  const handleSetRole = async () => {
    if (!selectedUserForRole || !newRole) return;
    try {
      const res = await authFetch(`${API}/admin/set-role`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: selectedUserForRole.user_id, role: newRole }),
      });
      if (res.ok) { toast.success("Role updated"); setRoleDialogOpen(false); fetchData(); }
      else toast.error("Failed to update role");
    } catch { toast.error("Error"); }
  };

  const handleCreateUser = async () => {
    if (!createForm.name || !createForm.email || !createForm.password) {
      toast.error("All fields are required"); return;
    }
    setCreating(true);
    try {
      const res = await authFetch(`${API}/admin/create-user`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(createForm),
      });
      if (res.ok) {
        toast.success("User created successfully");
        setCreateUserOpen(false);
        setCreateForm({ name: "", email: "", password: "", role: "client" });
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to create user");
      }
    } catch { toast.error("Error creating user"); } finally { setCreating(false); }
  };

  const handleViewThread = async (thread) => {
    setViewingThread(thread);
    try {
      const res = await authFetch(`${API}/messages/thread/${thread.thread_id}`);
      if (res.ok) setThreadMessages(await res.json());
    } catch { toast.error("Failed to load messages"); }
  };

  const handleDeleteUser = async () => {
    if (!deleteUserId) return;
    try {
      const res = await authFetch(`${API}/admin/users/${deleteUserId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("User deleted successfully");
        fetchData();
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to delete user");
      }
    } catch { toast.error("Error deleting user"); }
    setDeleteUserOpen(false);
    setDeleteUserId(null);
  };

  const handleDeleteThread = async () => {
    if (!deleteThreadId) return;
    try {
      const res = await authFetch(`${API}/admin/messages/thread/${deleteThreadId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Thread deleted");
        fetchData();
        setViewingThread(null);
      } else toast.error("Failed to delete thread");
    } catch { toast.error("Error deleting thread"); }
    setDeleteThreadOpen(false);
    setDeleteThreadId(null);
  };

  const handleUpdateInquiryStatus = async (inquiryId, newStatus) => {
    try {
      const res = await authFetch(`${API}/admin/inquiries/${inquiryId}/status`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ status: newStatus }),
      });
      if (res.ok) {
        toast.success(`Inquiry marked as ${newStatus}`);
        fetchData();
      } else toast.error("Failed to update status");
    } catch { toast.error("Error updating inquiry"); }
  };

  const handleDeleteInquiry = async () => {
    if (!deleteInquiryId) return;
    try {
      const res = await authFetch(`${API}/admin/inquiries/${deleteInquiryId}`, { method: "DELETE" });
      if (res.ok) {
        toast.success("Inquiry deleted");
        fetchData();
      } else toast.error("Failed to delete inquiry");
    } catch { toast.error("Error deleting inquiry"); }
    setDeleteInquiryOpen(false);
    setDeleteInquiryId(null);
  };

  const filteredClients = allClients.filter(
    (c) => {
      const matchesSearch = c.name?.toLowerCase().includes(clientSearch.toLowerCase()) ||
             c.email?.toLowerCase().includes(clientSearch.toLowerCase());
      if (filterTeamMemberId) {
        return matchesSearch && c.allocated_to === filterTeamMemberId;
      }
      return matchesSearch;
    }
  );
  const filteredUsers = allUsers.filter(
    (u) => u.name?.toLowerCase().includes(userSearch.toLowerCase()) ||
           u.email?.toLowerCase().includes(userSearch.toLowerCase())
  );

  // Service CRUD handlers
  const handleSaveService = async (svc) => {
    setSavingService(true);
    try {
      const isNew = !svc.service_id;
      const url = isNew ? `${API}/admin/services` : `${API}/admin/services/${svc.service_id}`;
      const res = await authFetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(svc),
      });
      if (res.ok) {
        toast.success(isNew ? "Service created" : "Service updated");
        setEditingService(null);
        setNewServiceOpen(false);
        setNewServiceForm({ name: "", category: "addon", description: "", base_price: "" });
        fetchData();
      } else toast.error("Failed to save");
    } catch { toast.error("Error saving service"); }
    finally { setSavingService(false); }
  };

  const handleDeleteService = async (serviceId) => {
    try {
      const res = await authFetch(`${API}/admin/services/${serviceId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Service deleted"); fetchData(); }
      else toast.error("Failed to delete");
    } catch { toast.error("Error"); }
  };

  const handleSaveRule = async (rule) => {
    setSavingService(true);
    try {
      const isNew = !rule.rule_id;
      const url = isNew ? `${API}/admin/complexity-rules` : `${API}/admin/complexity-rules/${rule.rule_id}`;
      const res = await authFetch(url, {
        method: isNew ? "POST" : "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(isNew ? rule : { amount: rule.amount, label: rule.label, is_active: rule.is_active }),
      });
      if (res.ok) {
        toast.success(isNew ? "Rule created" : "Rule updated");
        setEditingRule(null);
        setNewRuleOpen(false);
        setNewRuleForm({ rule_type: "addon", condition_key: "", label: "", amount: "" });
        fetchData();
      } else toast.error("Failed to save rule");
    } catch { toast.error("Error saving rule"); }
    finally { setSavingService(false); }
  };

  const handleDeleteRule = async (ruleId) => {
    try {
      const res = await authFetch(`${API}/admin/complexity-rules/${ruleId}`, { method: "DELETE" });
      if (res.ok) { toast.success("Rule deleted"); fetchData(); }
      else toast.error("Failed to delete");
    } catch { toast.error("Error"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: "var(--bg-primary)" }}>
        <div className="spinner"></div>
      </div>
    );
  }

  const svc = analytics?.service_breakdown || {};
  const chartData = [
    { name: "ITR Filing", value: svc.itr_filing?.revenue || 0, fill: "var(--accent)" },
    { name: "Accounting", value: svc.accounting?.revenue || 0, fill: "#F59E0B" },
    { name: "Combined", value: svc.combined?.revenue || 0, fill: "#8B5CF6" },
  ].filter((d) => d.value > 0);



  return (
    <PortalLayout title="Admin Dashboard">
      <div className="relative z-10 w-full pb-10">
        {/* Welcome */}
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mb-4 px-4 sm:px-6 mt-2">
          <h1 className="text-xl font-bold text-[#145886] flex items-center">
            CA Ronak Patel, Chartered Accountant — Admin Dashboard
          </h1>
        </motion.div>

        <div className="px-4 sm:px-6">
          <div className="flex-1 min-w-0 space-y-6">

          {/* ===================== ANALYTICS TAB ===================== */}
          {activeAdminTab === "analytics" && (<>
            {/* Top Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
              {[
                { label: "Total Revenue", value: `INR ${(analytics?.total_revenue || 0).toLocaleString()}`, icon: IndianRupee, color: "#10B981" },
                { label: "Total Clients", value: analytics?.total_clients || 0, icon: Users, color: "var(--accent)" },
                { label: "Team Members", value: analytics?.total_team_members || 0, icon: UserPlus, color: "#3B82F6" },
                { label: "Pending Allocation", value: analytics?.pending_allocation || 0, icon: Clock, color: analytics?.pending_allocation > 0 ? "#F59E0B" : "#10B981" },
              ].map((s) => (
                <div key={s.label} className="soft-card !p-5" data-testid={`analytics-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                      <s.icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>{s.label}</p>
                      <p className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>{s.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>

            {/* Second Row Stats */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-3">
              {[
                { label: "Documents", value: analytics?.total_documents || 0, icon: FileText, color: "#8B5CF6" },
                { label: "Transactions", value: analytics?.total_transactions || 0, icon: BookOpen, color: "#F59E0B" },
                { label: "Unread Messages", value: analytics?.unread_messages || 0, icon: MessageSquare, color: "#EF4444" },
                { label: "Completed Payments", value: analytics?.completed_payments || 0, icon: CheckCircle, color: "#10B981" },
              ].map((s) => (
                <div key={s.label} className="soft-card !p-5" data-testid={`analytics-${s.label.toLowerCase().replace(/\s/g, "-")}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-11 h-11 rounded-lg flex items-center justify-center" style={{ background: `${s.color}15` }}>
                      <s.icon className="w-5 h-5" style={{ color: s.color }} />
                    </div>
                    <div>
                      <p className="text-xs" style={{ color: "var(--text-muted)" }}>{s.label}</p>
                      <p className="text-xl font-bold" style={{ color: "var(--text-primary)" }}>{s.value}</p>
                    </div>
                  </div>
                </div>
              ))}
            </motion.div>

            <div className="grid lg:grid-cols-2 gap-3">
              {/* Revenue by Service */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="soft-card" data-testid="revenue-by-service">
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                  <TrendingUp className="w-5 h-5 text-[var(--accent)]" /> Revenue by Service
                </h3>
                <div className="space-y-4">
                  {[
                    { label: "ITR Filing", data: svc.itr_filing, color: "var(--accent)" },
                    { label: "Accounting", data: svc.accounting, color: "#F59E0B" },
                    { label: "Combined", data: svc.combined, color: "#8B5CF6" },
                  ].map((item) => {
                    const pct = analytics?.total_revenue ? ((item.data?.revenue || 0) / analytics.total_revenue * 100) : 0;
                    return (
                      <div key={item.label}>
                        <div className="flex justify-between mb-1">
                          <span className="text-sm" style={{ color: "var(--text-secondary)" }}>{item.label} ({item.data?.count || 0} clients)</span>
                          <span className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>INR {(item.data?.revenue || 0).toLocaleString()}</span>
                        </div>
                        <div className="h-2.5 rounded-full" style={{ background: "var(--bg-tertiary)" }}>
                          <div className="h-full rounded-full transition-all duration-700" style={{ width: `${pct}%`, background: item.color, minWidth: pct > 0 ? "8px" : "0" }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>

              {/* Monthly Revenue Trend */}
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }} className="soft-card" data-testid="monthly-revenue-chart">
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                  <IndianRupee className="w-5 h-5 text-[var(--accent)]" /> Monthly Revenue Trend
                </h3>
                {analytics?.monthly_trend?.some((m) => m.revenue > 0) ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={analytics.monthly_trend} margin={{ top: 5, right: 5, bottom: 5, left: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--border-color)" />
                      <XAxis dataKey="month" tick={{ fill: "var(--text-muted)", fontSize: 11 }} tickFormatter={(v) => v.slice(5)} />
                      <YAxis tick={{ fill: "var(--text-muted)", fontSize: 11 }} tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} />
                      <Tooltip
                        contentStyle={{ background: "var(--bg-secondary)", border: "1px solid var(--border-color)", borderRadius: "8px", color: "var(--text-primary)" }}
                        formatter={(v) => [`INR ${v.toLocaleString()}`, "Revenue"]}
                      />
                      <Bar dataKey="revenue" radius={[4, 4, 0, 0]}>
                        {analytics.monthly_trend.map((_, i) => (
                          <Cell key={i} fill={i === analytics.monthly_trend.length - 1 ? "var(--accent)" : "var(--accent)"} fillOpacity={0.3 + (i / analytics.monthly_trend.length) * 0.7} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex items-center justify-center h-[220px]">
                    <p style={{ color: "var(--text-muted)" }}>No revenue data yet</p>
                  </div>
                )}
              </motion.div>
            </div>
          </>)}

          {/* ===================== ALLOCATION TAB ===================== */}
          {activeAdminTab === "allocation" && (
            tabLoading["allocation"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card" data-testid="pending-allocation-section">
              <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
                Pending Client Allocation
              </h2>
              {pendingClients.filter((c) => c.has_payment).length > 0 ? (
                <div className="space-y-4">
                  {pendingClients.filter((c) => c.has_payment).map((client) => (
                    <div
                      key={client.user_id}
                      className={`p-4 border rounded-lg flex items-center justify-between transition-all ${
                        selectedClient === client.user_id ? "border-[var(--accent)]" : ""
                      }`}
                      style={{ borderColor: selectedClient === client.user_id ? "var(--accent)" : "var(--border-color)", background: selectedClient === client.user_id ? "var(--accent-bg)" : "var(--bg-tertiary)" }}
                    >
                      <div className="flex items-center gap-4">
                        <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--accent-bg)" }}>
                          <User className="w-5 h-5 text-[var(--accent)]" />
                        </div>
                        <div>
                          <p className="font-medium" style={{ color: "var(--text-primary)" }}>{client.name}</p>
                          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{client.email}</p>
                        </div>
                        <span className="px-2 py-1 text-xs rounded-md" style={{ background: "var(--accent-bg)", color: "var(--accent)" }}>
                          {client.service_type === "itr_filing" ? "ITR Filing" : client.service_type === "accounting" ? "Accounting" : "Combined"}
                        </span>
                      </div>
                      <Button
                        variant={selectedClient === client.user_id ? "default" : "outline"}
                        size="sm"
                        onClick={() => setSelectedClient(client.user_id)}
                        className="rounded-md"
                        data-testid={`select-client-${client.user_id}`}
                      >
                        {selectedClient === client.user_id ? "Selected" : "Select"}
                      </Button>
                    </div>
                  ))}
                  {selectedClient && (
                    <div className="mt-4 p-4 rounded-lg border" style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}>
                      <h3 className="font-semibold mb-3" style={{ color: "var(--text-primary)" }}>Assign to Team Member</h3>
                      <div className="flex gap-4">
                        <Select value={selectedTeamMember} onValueChange={setSelectedTeamMember}>
                          <SelectTrigger className="flex-1 rounded-md" data-testid="team-member-select">
                            <SelectValue placeholder="Select team member" />
                          </SelectTrigger>
                          <SelectContent>
                            {teamMembers.map((tm) => (
                              <SelectItem key={tm.user_id} value={tm.user_id}>{tm.name} ({tm.email})</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button onClick={handleAllocateClient} disabled={allocating || !selectedTeamMember} className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md" data-testid="allocate-btn">
                          {allocating ? "Allocating..." : "Allocate"}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-[#10B981]" />
                  <p style={{ color: "var(--text-secondary)" }}>All clients have been allocated</p>
                </div>
              )}
            </motion.div>
            )
          )}

          {/* ===================== ALL CLIENTS TAB ===================== */}
          {activeAdminTab === "clients" && (
            tabLoading["clients"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card" data-testid="all-clients-section">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <h2 className="font-['Playfair_Display'] text-xl font-bold" style={{ color: "var(--text-primary)" }}>All Clients</h2>
                  {filterTeamMemberId && (
                    <button onClick={() => setFilterTeamMemberId(null)} className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-full bg-[var(--accent-bg)] text-[var(--accent)] hover:bg-[var(--accent)] hover:text-white transition-colors" data-testid="clear-team-filter">
                      Filtered by team member <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
                  <Input value={clientSearch} onChange={(e) => setClientSearch(e.target.value)} placeholder="Search clients..." className="pl-9 w-64 rounded-md" data-testid="search-clients" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead className="text-right">Payment</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead className="text-center">Profiles</TableHead>
                      <TableHead className="text-center">Docs</TableHead>
                      <TableHead className="text-center">Txns</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">View</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredClients.map((client) => (
                      <TableRow key={client.user_id} className="cursor-pointer hover:bg-[var(--accent-bg)] transition-colors" onClick={() => navigate(`/team/client/${client.user_id}`)} data-testid={`client-row-${client.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {client.picture ? (
                              <img src={client.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--accent-bg)" }}>
                                <User className="w-4 h-4 text-[var(--accent)]" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium" style={{ color: "var(--text-primary)" }}>{client.name}</p>
                              <p className="text-xs" style={{ color: "var(--text-muted)" }}>{client.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          {client.payment?.service_type ? (
                            <span className="px-2 py-1 text-xs rounded-md" style={{ background: "var(--accent-bg)", color: "var(--text-secondary)" }}>
                              {client.payment.service_type === "itr_filing" ? "ITR" : client.payment.service_type === "accounting" ? "Accounting" : "Combined"}
                            </span>
                          ) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                        </TableCell>
                        <TableCell className="text-right">
                          {client.payment?.total_amount ? (
                            <span className="font-medium text-[#10B981]">INR {client.payment.total_amount.toLocaleString()}</span>
                          ) : <span style={{ color: "var(--text-muted)" }}>-</span>}
                        </TableCell>
                        <TableCell>
                          {client.team_member ? (
                            <span style={{ color: "var(--text-primary)" }}>{client.team_member.name}</span>
                          ) : <span className="text-[#F59E0B]">Unassigned</span>}
                        </TableCell>
                        <TableCell className="text-center" style={{ color: "var(--text-secondary)" }}>{client.profile_count}</TableCell>
                        <TableCell className="text-center" style={{ color: "var(--text-secondary)" }}>{client.document_count}</TableCell>
                        <TableCell className="text-center" style={{ color: "var(--text-secondary)" }}>{client.transaction_count}</TableCell>
                        <TableCell>
                          <span className={`px-2 py-1 text-xs rounded-md ${client.allocated_to ? "bg-[#10B981]/15 text-[#10B981]" : "bg-[#F59E0B]/15 text-[#F59E0B]"}`}>
                            {client.allocated_to ? "Active" : "Pending"}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <ChevronRight className="w-4 h-4 inline" style={{ color: "var(--text-muted)" }} />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </motion.div>
            )
          )}

          {/* ===================== TEAM PERFORMANCE TAB ===================== */}
          {activeAdminTab === "team" && (
            tabLoading["team"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card" data-testid="team-performance-section">
              <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
                Team Member Performance
              </h2>
              {teamPerformance.length > 0 ? (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Team Member</TableHead>
                      <TableHead className="text-center">Assigned Clients</TableHead>
                      <TableHead className="text-center">Documents</TableHead>
                      <TableHead className="text-center">Transactions</TableHead>
                      <TableHead className="text-center">Pending Tasks</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {teamPerformance.map((tm) => (
                      <TableRow key={tm.user_id} data-testid={`team-row-${tm.user_id}`}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            {tm.picture ? (
                              <img src={tm.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                            ) : (
                              <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--accent-bg)" }}>
                                <User className="w-4 h-4 text-[var(--accent)]" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium" style={{ color: "var(--text-primary)" }}>{tm.name}</p>
                              <p className="text-xs" style={{ color: "var(--text-muted)" }}>{tm.email}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="text-lg font-bold" style={{ color: "var(--text-primary)" }}>{tm.assigned_clients}</span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="flex items-center justify-center gap-1" style={{ color: "var(--text-secondary)" }}>
                            <FileText className="w-3.5 h-3.5" /> {tm.total_documents}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className="flex items-center justify-center gap-1" style={{ color: "var(--text-secondary)" }}>
                            <BookOpen className="w-3.5 h-3.5" /> {tm.total_transactions}
                          </span>
                        </TableCell>
                        <TableCell className="text-center">
                          <span className={`px-2.5 py-1 text-xs font-medium rounded-md ${tm.pending_tasks > 0 ? "bg-[#F59E0B]/15 text-[#F59E0B]" : "bg-[#10B981]/15 text-[#10B981]"}`}>
                            {tm.pending_tasks}
                          </span>
                        </TableCell>
                        <TableCell className="text-right">
                          <Button variant="ghost" size="sm" onClick={() => {
                            setFilterTeamMemberId(tm.user_id);
                            setClientSearch("");
                            navigate("/admin/clients");
                          }} data-testid={`view-clients-${tm.user_id}`}>
                            <Eye className="w-4 h-4 mr-1" /> View Clients
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                  <p style={{ color: "var(--text-secondary)" }}>No team members yet</p>
                </div>
              )}
            </motion.div>
            )
          )}

          {/* ===================== USER MANAGEMENT TAB ===================== */}
          {activeAdminTab === "users" && (
            tabLoading["users"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card" data-testid="user-management-section">
              <div className="flex items-center justify-between mb-3">
                <h2 className="font-['Playfair_Display'] text-xl font-bold" style={{ color: "var(--text-primary)" }}>User Management</h2>
                <div className="flex items-center gap-3">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4" style={{ color: "var(--text-muted)" }} />
                    <Input value={userSearch} onChange={(e) => setUserSearch(e.target.value)} placeholder="Search users..." className="pl-9 w-56 rounded-md" data-testid="search-users" />
                  </div>
                  <Button onClick={() => setCreateUserOpen(true)} className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md" data-testid="create-user-btn">
                    <Plus className="w-4 h-4 mr-2" /> Create User
                  </Button>
                </div>
              </div>
              <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>Role</TableHead>
                    <TableHead>Joined</TableHead>
                    <TableHead>Last Active</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.user_id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {u.picture ? (
                            <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover" />
                          ) : (
                            <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ background: "var(--accent-bg)" }}>
                              <User className="w-4 h-4 text-[var(--accent)]" />
                            </div>
                          )}
                          <div>
                            <p className="font-medium" style={{ color: "var(--text-primary)" }}>{u.name}</p>
                            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`px-2 py-1 text-xs rounded-md font-medium ${
                          u.role === "super_admin" ? "bg-[var(--accent)] text-white" :
                          u.role === "team_member" ? "bg-[#3B82F6]/15 text-[#3B82F6]" :
                          "bg-[var(--bg-tertiary)]"
                        }`} style={u.role === "client" ? { color: "var(--text-secondary)" } : {}}>
                          {u.role === "super_admin" ? "Super Admin" : u.role === "team_member" ? "Team Member" : "Client"}
                        </span>
                      </TableCell>
                      <TableCell style={{ color: "var(--text-secondary)" }}>{new Date(u.created_at).toLocaleDateString("en-IN")}</TableCell>
                      <TableCell style={{ color: "var(--text-secondary)" }}>
                        {u.last_active_date ? (
                          <span className="text-sm">{new Date(u.last_active_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "2-digit" })}</span>
                        ) : (
                          <span className="text-xs px-2 py-0.5 rounded bg-[#F59E0B]/10 text-[#F59E0B]">Never</span>
                        )}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-2">
                          <Button variant="outline" size="sm" onClick={() => { setSelectedUserForRole(u); setNewRole(u.role); setRoleDialogOpen(true); }} className="rounded-md" data-testid={`edit-role-${u.user_id}`}>
                            <Settings className="w-4 h-4 mr-1" /> Edit Role
                          </Button>
                          {u.role !== "super_admin" && (
                            <Button variant="outline" size="sm" onClick={() => { setDeleteUserId(u.user_id); setDeleteUserName(u.name || u.email); setDeleteUserOpen(true); }} className="rounded-md text-red-400 hover:text-red-300 hover:border-red-400/50" data-testid={`delete-user-${u.user_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </motion.div>
            )
          )}

          {/* ===================== GLOBAL INBOX TAB ===================== */}
          {activeAdminTab === "messages" && (
            tabLoading["messages"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card" data-testid="global-inbox-section">
              <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>
                Global Inbox
              </h2>
              {allThreads.length > 0 ? (
                <div className="space-y-3">
                  {allThreads.map((thread) => {
                    const p1 = thread.participant_1;
                    const p2 = thread.participant_2;
                    return (
                      <div
                        key={thread.thread_id}
                        className="p-4 rounded-lg border cursor-pointer hover:border-[var(--accent)] transition-all"
                        style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}
                        onClick={() => handleViewThread(thread)}
                        data-testid={`thread-${thread.thread_id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="flex -space-x-2">
                              {[p1, p2].map((p, i) => p?.picture ? (
                                <img key={i} src={p.picture} alt="" className="w-8 h-8 rounded-full object-cover ring-2 ring-[var(--bg-tertiary)]" />
                              ) : (
                                <div key={i} className="w-8 h-8 rounded-full flex items-center justify-center ring-2 ring-[var(--bg-tertiary)]" style={{ background: "var(--accent-bg)" }}>
                                  <User className="w-3.5 h-3.5 text-[var(--accent)]" />
                                </div>
                              ))}
                            </div>
                            <div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium text-sm" style={{ color: "var(--text-primary)" }}>{p1?.name || "?"}</span>
                                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                                  ({p1?.role === "team_member" ? "Team" : p1?.role === "super_admin" ? "Admin" : "Client"})
                                </span>
                                <ChevronRight className="w-3 h-3" style={{ color: "var(--text-muted)" }} />
                                <span className="font-medium text-sm" style={{ color: "var(--text-primary)" }}>{p2?.name || "?"}</span>
                                <span className="text-xs" style={{ color: "var(--text-muted)" }}>
                                  ({p2?.role === "team_member" ? "Team" : p2?.role === "super_admin" ? "Admin" : "Client"})
                                </span>
                              </div>
                              <p className="text-xs truncate max-w-md mt-0.5" style={{ color: "var(--text-secondary)" }}>{thread.last_message}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3 text-right shrink-0">
                            <div>
                              <p className="text-xs" style={{ color: "var(--text-muted)" }}>
                                {new Date(thread.last_time).toLocaleDateString("en-IN")}
                              </p>
                              <span className="text-xs px-1.5 py-0.5 rounded" style={{ background: "var(--accent-bg)", color: "var(--text-secondary)" }}>
                                {thread.message_count} msgs
                              </span>
                            </div>
                            <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); setDeleteThreadId(thread.thread_id); setDeleteThreadOpen(true); }} className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-md" data-testid={`delete-thread-${thread.thread_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-12">
                  <MessageSquare className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                  <p style={{ color: "var(--text-secondary)" }}>No message threads yet</p>
                </div>
              )}
            </motion.div>
            )
          )}

          {/* ===================== INQUIRIES TAB ===================== */}
          {activeAdminTab === "inquiries" && (
            tabLoading["inquiries"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="soft-card" data-testid="inquiries-section">
              <h2 className="font-['Playfair_Display'] text-xl font-bold mb-3" style={{ color: "var(--text-primary)" }}>Inquiries</h2>
              {inquiries.length > 0 ? (
                <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {inquiries.map((inq) => {
                      const status = inq.status || "unread";
                      const statusColors = {
                        unread: "bg-[#F59E0B]/15 text-[#F59E0B]",
                        read: "bg-[#3B82F6]/15 text-[#3B82F6]",
                        replied: "bg-[#10B981]/15 text-[#10B981]",
                      };
                      return (
                        <TableRow key={inq.inquiry_id}>
                          <TableCell className="font-medium" style={{ color: "var(--text-primary)" }}>{inq.name}</TableCell>
                          <TableCell>
                            <p className="text-sm" style={{ color: "var(--text-primary)" }}>{inq.email}</p>
                            <p className="text-xs" style={{ color: "var(--text-muted)" }}>{inq.phone}</p>
                          </TableCell>
                          <TableCell>
                            <span className="px-2 py-1 text-xs rounded-md" style={{ background: "var(--accent-bg)", color: "var(--text-secondary)" }}>{inq.service_type}</span>
                          </TableCell>
                          <TableCell>
                            <div className="max-w-[250px] space-y-1">
                              <p className="truncate" style={{ color: "var(--text-secondary)" }} title={inq.message}>{inq.message}</p>
                              {inq.ai_category && (
                                <div className="mt-2 p-2 rounded border text-xs" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
                                  <div className="flex justify-between items-center mb-1">
                                    <span className="font-semibold" style={{ color: "var(--accent)" }}>AI Classification: {inq.ai_category}</span>
                                    <span className="px-1.5 py-0.5 rounded text-[10px]" style={{ background: "var(--accent-bg)", color: "var(--accent)" }}>
                                      {inq.ai_confidence || "low"} conf
                                    </span>
                                  </div>
                                  {inq.ai_draft_reply && (
                                    <p className="mt-1 italic" style={{ color: "var(--text-muted)", fontSize: "10px" }}>
                                      Draft: {inq.ai_draft_reply}
                                    </p>
                                  )}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <select
                              value={status}
                              onChange={(e) => handleUpdateInquiryStatus(inq.inquiry_id, e.target.value)}
                              className={`px-2 py-1 text-xs rounded-md font-medium cursor-pointer border-0 outline-none ${statusColors[status] || ""}`}
                              style={{ background: "transparent" }}
                              data-testid={`inquiry-status-${inq.inquiry_id}`}
                            >
                              <option value="unread">Unread</option>
                              <option value="read">Read</option>
                              <option value="replied">Replied</option>
                            </select>
                          </TableCell>
                          <TableCell style={{ color: "var(--text-muted)" }}>{new Date(inq.created_at).toLocaleDateString("en-IN")}</TableCell>
                          <TableCell className="text-right">
                            <Button variant="ghost" size="sm" onClick={() => { setDeleteInquiryId(inq.inquiry_id); setDeleteInquiryOpen(true); }} className="text-red-400 hover:text-red-300 hover:bg-red-400/10 rounded-md" data-testid={`delete-inquiry-${inq.inquiry_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
                </div>
              ) : (
                <div className="text-center py-12">
                  <Mail className="w-12 h-12 mx-auto mb-3" style={{ color: "var(--text-muted)" }} />
                  <p style={{ color: "var(--text-secondary)" }}>No inquiries yet</p>
                </div>
              )}
            </motion.div>
            )
          )}

          {/* ===================== SERVICES & PRICING TAB ===================== */}
          {activeAdminTab === "services" && (
            tabLoading["services"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-8">
              {/* Base Services */}
              <div className="soft-card border-[var(--border-light)]" style={{ borderColor: "var(--border-color)" }}>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                    <Settings className="w-5 h-5" style={{ color: "var(--accent)" }} /> Base Services
                  </h3>
                  <Button
                    size="sm"
                    onClick={() => setNewServiceOpen(true)}
                    className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
                    data-testid="add-service-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Service
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Service Name</TableHead>
                        <TableHead>Category</TableHead>
                        <TableHead>Description</TableHead>
                        <TableHead className="text-right">Base Price (INR)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {services.map((svc) => (
                        <TableRow key={svc.service_id}>
                          <TableCell className="font-medium">{editingService?.service_id === svc.service_id ?
                            <Input value={editingService.name} onChange={(e) => setEditingService({...editingService, name: e.target.value})} className="h-8 w-40" /> : svc.name
                          }</TableCell>
                          <TableCell>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--accent-bg)]" style={{ color: "var(--accent)" }}>
                              {svc.category}
                            </span>
                          </TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate" style={{ color: "var(--text-secondary)" }}>
                            {editingService?.service_id === svc.service_id ?
                              <Input value={editingService.description} onChange={(e) => setEditingService({...editingService, description: e.target.value})} className="h-8" /> : svc.description
                            }
                          </TableCell>
                          <TableCell className="text-right font-bold">
                            {editingService?.service_id === svc.service_id ?
                              <Input type="number" value={editingService.base_price} onChange={(e) => setEditingService({...editingService, base_price: e.target.value})} className="h-8 w-28 text-right" /> :
                              <span style={{ color: "var(--accent)" }}>INR {svc.base_price?.toLocaleString()}</span>
                            }
                          </TableCell>
                          <TableCell>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${svc.is_active ? "bg-[#55B848]/10 text-[#55B848]" : "bg-red-100 text-red-600"}`}>
                              {svc.is_active ? "Active" : "Inactive"}
                            </span>
                          </TableCell>
                          <TableCell className="text-right">
                            {editingService?.service_id === svc.service_id ? (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" disabled={savingService} onClick={() => handleSaveService(editingService)} className="text-[#55B848]" data-testid={`save-svc-${svc.service_id}`}>
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingService(null)}><X className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => setEditingService({...svc})} data-testid={`edit-svc-${svc.service_id}`}>
                                  <Settings className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteService(svc.service_id)} data-testid={`delete-svc-${svc.service_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* New Service inline form */}
                {newServiceOpen && (
                  <div className="mt-4 p-4 rounded-lg border border-dashed" style={{ borderColor: "var(--accent)", background: "var(--accent-bg)" }}>
                    <h4 className="text-sm font-bold mb-3" style={{ color: "var(--accent)" }}>New Service</h4>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                      <Input placeholder="Service Name" value={newServiceForm.name} onChange={(e) => setNewServiceForm({...newServiceForm, name: e.target.value})} data-testid="new-svc-name" />
                      <select value={newServiceForm.category} onChange={(e) => setNewServiceForm({...newServiceForm, category: e.target.value})}
                        className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}>
                        <option value="itr_filing">ITR Filing</option>
                        <option value="accounting">Accounting</option>
                        <option value="both">Combined</option>
                        <option value="addon">Add-on Service</option>
                      </select>
                      <Input type="number" placeholder="Base Price" value={newServiceForm.base_price} onChange={(e) => setNewServiceForm({...newServiceForm, base_price: e.target.value})} data-testid="new-svc-price" />
                      <div className="flex gap-2">
                        <Button size="sm" disabled={!newServiceForm.name || !newServiceForm.base_price || savingService}
                          onClick={() => handleSaveService(newServiceForm)}
                          className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]" data-testid="save-new-svc-btn">
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setNewServiceOpen(false); setNewServiceForm({ name: "", category: "addon", description: "", base_price: "" }); }}>Cancel</Button>
                      </div>
                    </div>
                    <Input className="mt-2" placeholder="Description (optional)" value={newServiceForm.description} onChange={(e) => setNewServiceForm({...newServiceForm, description: e.target.value})} />
                  </div>
                )}
              </div>

              {/* Complexity Rules */}
              <div className="soft-card border-[var(--border-light)]" style={{ borderColor: "var(--border-color)" }}>
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
                      <TrendingUp className="w-5 h-5" style={{ color: "var(--accent)" }} /> Complexity & Add-on Pricing
                    </h3>
                    <p className="text-sm mt-1" style={{ color: "var(--text-secondary)" }}>
                      Rules applied on top of base service prices during fee calculation.
                    </p>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => setNewRuleOpen(true)}
                    className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]"
                    data-testid="add-rule-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Rule
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Label</TableHead>
                        <TableHead>Condition</TableHead>
                        <TableHead className="text-right">Amount (INR)</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {complexityRules.map((rule) => (
                        <TableRow key={rule.rule_id}>
                          <TableCell>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[var(--bg-tertiary)]" style={{ color: "var(--text-secondary)" }}>
                              {rule.rule_type}
                            </span>
                          </TableCell>
                          <TableCell className="font-medium">
                            {editingRule?.rule_id === rule.rule_id ?
                              <Input value={editingRule.label} onChange={(e) => setEditingRule({...editingRule, label: e.target.value})} className="h-8 w-48" /> : rule.label
                            }
                          </TableCell>
                          <TableCell className="text-sm" style={{ color: "var(--text-muted)" }}>{rule.condition_key}</TableCell>
                          <TableCell className="text-right font-bold">
                            {editingRule?.rule_id === rule.rule_id ?
                              <Input type="number" value={editingRule.amount} onChange={(e) => setEditingRule({...editingRule, amount: Number(e.target.value)})} className="h-8 w-28 text-right" /> :
                              <span style={{ color: "var(--accent)" }}>INR {rule.amount?.toLocaleString()}</span>
                            }
                          </TableCell>
                          <TableCell>
                            {editingRule?.rule_id === rule.rule_id ? (
                              <select value={editingRule.is_active ? "active" : "inactive"} onChange={(e) => setEditingRule({...editingRule, is_active: e.target.value === "active"})}
                                className="rounded border px-2 py-1 text-xs" style={{ borderColor: "var(--border-color)" }}>
                                <option value="active">Active</option>
                                <option value="inactive">Inactive</option>
                              </select>
                            ) : (
                              <span className={`text-xs px-2 py-0.5 rounded-full ${rule.is_active ? "bg-[#55B848]/10 text-[#55B848]" : "bg-red-100 text-red-600"}`}>
                                {rule.is_active ? "Active" : "Inactive"}
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-right">
                            {editingRule?.rule_id === rule.rule_id ? (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" disabled={savingService} onClick={() => handleSaveRule(editingRule)} className="text-[#55B848]">
                                  <CheckCircle className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingRule(null)}><X className="w-4 h-4" /></Button>
                              </div>
                            ) : (
                              <div className="flex gap-1 justify-end">
                                <Button size="sm" variant="ghost" onClick={() => setEditingRule({...rule})} data-testid={`edit-rule-${rule.rule_id}`}>
                                  <Settings className="w-4 h-4" />
                                </Button>
                                <Button size="sm" variant="ghost" className="text-red-500 hover:text-red-700" onClick={() => handleDeleteRule(rule.rule_id)} data-testid={`delete-rule-${rule.rule_id}`}>
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>

                {/* New Rule inline form */}
                {newRuleOpen && (
                  <div className="mt-4 p-4 rounded-lg border border-dashed" style={{ borderColor: "var(--accent)", background: "var(--accent-bg)" }}>
                    <h4 className="text-sm font-bold mb-3" style={{ color: "var(--accent)" }}>New Complexity Rule</h4>
                    <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                      <select value={newRuleForm.rule_type} onChange={(e) => setNewRuleForm({...newRuleForm, rule_type: e.target.value})}
                        className="rounded-md border px-3 py-2 text-sm" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}
                        data-testid="new-rule-type">
                        <option value="income_source">Income Source</option>
                        <option value="turnover">Turnover</option>
                        <option value="transaction_volume">Transaction Volume</option>
                        <option value="addon">Add-on</option>
                      </select>
                      <Input placeholder="Condition Key (e.g. crypto)" value={newRuleForm.condition_key} onChange={(e) => setNewRuleForm({...newRuleForm, condition_key: e.target.value})} data-testid="new-rule-key" />
                      <Input placeholder="Display Label" value={newRuleForm.label} onChange={(e) => setNewRuleForm({...newRuleForm, label: e.target.value})} data-testid="new-rule-label" />
                      <Input type="number" placeholder="Amount (INR)" value={newRuleForm.amount} onChange={(e) => setNewRuleForm({...newRuleForm, amount: e.target.value})} data-testid="new-rule-amount" />
                      <div className="flex gap-2">
                        <Button size="sm" disabled={!newRuleForm.condition_key || !newRuleForm.label || !newRuleForm.amount || savingService}
                          onClick={() => handleSaveRule(newRuleForm)}
                          className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)]" data-testid="save-new-rule-btn">
                          Save
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => { setNewRuleOpen(false); setNewRuleForm({ rule_type: "addon", condition_key: "", label: "", amount: "" }); }}>Cancel</Button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </motion.div>
            )
          )}

          {/* ===================== DISCOUNT CODES TAB ===================== */}
          {activeAdminTab === "discounts" && (
            tabLoading["discounts"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>Discount Codes</h3>
                <Button size="sm" className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md" onClick={() => setNewDiscountOpen(true)} data-testid="add-discount-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Discount Code
                </Button>
              </div>

              {discountCodes.length === 0 ? (
                <p className="text-center py-8" style={{ color: "var(--text-secondary)" }}>No discount codes yet. Create one to offer clients special pricing.</p>
              ) : (
                <div className="space-y-3">
                  {discountCodes.map((dc) => (
                    <div key={dc.code_id} className="soft-card !p-4 border-[var(--border-light)] flex items-center justify-between" style={{ borderColor: "var(--border-color)" }} data-testid={`discount-${dc.code}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-1">
                          <span className="font-mono font-bold text-lg px-3 py-1 rounded" style={{ background: "var(--accent-bg)", color: "var(--accent)" }}>{dc.code}</span>
                          <span className="text-sm font-semibold" style={{ color: dc.is_active ? "var(--success)" : "var(--text-secondary)" }}>
                            {dc.is_active ? "Active" : "Inactive"}
                          </span>
                        </div>
                        <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
                          {dc.discount_type === "percentage" ? `${dc.discount_value}% off` : `Flat INR${dc.discount_value} off`}
                          {dc.description ? ` — ${dc.description}` : ""}
                        </p>
                        <p className="text-xs mt-1" style={{ color: "var(--text-secondary)" }}>
                          Used: {dc.times_used || 0}{dc.max_uses > 0 ? `/${dc.max_uses}` : " (unlimited)"}
                          {dc.min_amount > 0 ? ` | Min: INR${dc.min_amount}` : ""}
                          {dc.expires_at ? ` | Expires: ${dc.expires_at.split("T")[0]}` : ""}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button size="sm" variant="ghost" onClick={async () => {
                          const res = await authFetch(`${API}/admin/discount-codes/${dc.code_id}`, {
                            method: "PUT", headers: { "Content-Type": "application/json" },
                            body: JSON.stringify({ is_active: !dc.is_active }),
                          });
                          if (res.ok) { toast.success(dc.is_active ? "Deactivated" : "Activated"); fetchData(); }
                        }} data-testid={`toggle-discount-${dc.code}`}>
                          {dc.is_active ? "Deactivate" : "Activate"}
                        </Button>
                        <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                          const res = await authFetch(`${API}/admin/discount-codes/${dc.code_id}`, { method: "DELETE" });
                          if (res.ok) { toast.success("Deleted"); fetchData(); }
                        }} data-testid={`delete-discount-${dc.code}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {newDiscountOpen && (
                <div className="mt-4 soft-card !p-4 border-[var(--border-light)] space-y-3" style={{ borderColor: "var(--accent)", background: "var(--bg-secondary)" }}>
                  <h4 className="font-semibold" style={{ color: "var(--text-primary)" }}>New Discount Code</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Code *</Label>
                      <Input value={newDiscountForm.code} onChange={(e) => setNewDiscountForm({...newDiscountForm, code: e.target.value.toUpperCase()})}
                        placeholder="e.g. WELCOME20" className="rounded-md" data-testid="discount-code-input" />
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Description</Label>
                      <Input value={newDiscountForm.description} onChange={(e) => setNewDiscountForm({...newDiscountForm, description: e.target.value})}
                        placeholder="Welcome discount" className="rounded-md" />
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Type *</Label>
                      <select value={newDiscountForm.discount_type} onChange={(e) => setNewDiscountForm({...newDiscountForm, discount_type: e.target.value})}
                        className="w-full h-9 rounded-md border px-3 text-sm" style={{ borderColor: "var(--border-color)", background: "var(--bg-primary)", color: "var(--text-primary)" }}>
                        <option value="percentage">Percentage (%)</option>
                        <option value="flat">Flat Amount (INR)</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Value *</Label>
                      <Input type="number" value={newDiscountForm.discount_value} onChange={(e) => setNewDiscountForm({...newDiscountForm, discount_value: e.target.value})}
                        placeholder={newDiscountForm.discount_type === "percentage" ? "e.g. 20" : "e.g. 500"} className="rounded-md" data-testid="discount-value-input" />
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Max Uses (0 = unlimited)</Label>
                      <Input type="number" value={newDiscountForm.max_uses} onChange={(e) => setNewDiscountForm({...newDiscountForm, max_uses: e.target.value})}
                        placeholder="0" className="rounded-md" />
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Min Amount (INR)</Label>
                      <Input type="number" value={newDiscountForm.min_amount} onChange={(e) => setNewDiscountForm({...newDiscountForm, min_amount: e.target.value})}
                        placeholder="0" className="rounded-md" />
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Expiry Date</Label>
                      <Input type="date" value={newDiscountForm.expires_at} onChange={(e) => setNewDiscountForm({...newDiscountForm, expires_at: e.target.value})}
                        className="rounded-md" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[var(--accent)] text-white rounded-md" data-testid="save-discount-btn" onClick={async () => {
                      if (!newDiscountForm.code || !newDiscountForm.discount_value) { toast.error("Code and value are required"); return; }
                      const payload = { ...newDiscountForm, discount_value: parseFloat(newDiscountForm.discount_value),
                        max_uses: parseInt(newDiscountForm.max_uses || "0"), min_amount: parseFloat(newDiscountForm.min_amount || "0"),
                        expires_at: newDiscountForm.expires_at ? new Date(newDiscountForm.expires_at).toISOString() : null };
                      const res = await authFetch(`${API}/admin/discount-codes`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
                      if (res.ok) { toast.success("Discount code created"); setNewDiscountOpen(false); setNewDiscountForm({ code: "", description: "", discount_type: "percentage", discount_value: "", max_uses: "", min_amount: "", expires_at: "" }); fetchData(); }
                      else { const err = await res.json(); toast.error(err.detail || "Failed to create"); }
                    }}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setNewDiscountOpen(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </motion.div>
            )
          )}

          {/* ===================== CUSTOM RATES TAB ===================== */}
          {activeAdminTab === "custom-rates" && (
            tabLoading["custom-rates"] ? <TabSpinner /> : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold" style={{ color: "var(--text-primary)" }}>Client Custom Rates</h3>
                <Button size="sm" className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md" onClick={() => setNewRateOpen(true)} data-testid="add-custom-rate-btn">
                  <Plus className="w-4 h-4 mr-1" /> Set Custom Rate
                </Button>
              </div>
              <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>Override the default service price for specific clients. Custom rates take priority over standard pricing.</p>

              {customRates.length === 0 ? (
                <p className="text-center py-8" style={{ color: "var(--text-secondary)" }}>No custom rates set. All clients use standard pricing.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b" style={{ borderColor: "var(--border-color)" }}>
                        <th className="text-left py-2 px-3 font-semibold" style={{ color: "var(--text-primary)" }}>Client</th>
                        <th className="text-left py-2 px-3 font-semibold" style={{ color: "var(--text-primary)" }}>Service</th>
                        <th className="text-right py-2 px-3 font-semibold" style={{ color: "var(--text-primary)" }}>Custom Price</th>
                        <th className="text-left py-2 px-3 font-semibold" style={{ color: "var(--text-primary)" }}>Notes</th>
                        <th className="text-right py-2 px-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {customRates.map((cr) => (
                        <tr key={cr.rate_id} className="border-b" style={{ borderColor: "var(--border-color)" }} data-testid={`rate-${cr.rate_id}`}>
                          <td className="py-2 px-3" style={{ color: "var(--text-primary)" }}>{cr.client_name || cr.user_id}<br/><span className="text-xs" style={{ color: "var(--text-secondary)" }}>{cr.client_email || ""}</span></td>
                          <td className="py-2 px-3" style={{ color: "var(--text-primary)" }}>{cr.service_category === "itr_filing" ? "ITR Filing" : cr.service_category === "accounting" ? "Accounting" : cr.service_category === "both" ? "Combined" : cr.service_category}</td>
                          <td className="py-2 px-3 text-right font-semibold" style={{ color: "var(--accent)" }}>INR{cr.custom_price?.toLocaleString()}</td>
                          <td className="py-2 px-3 text-sm" style={{ color: "var(--text-secondary)" }}>{cr.notes || "—"}</td>
                          <td className="py-2 px-3 text-right">
                            <Button size="sm" variant="ghost" className="text-red-500" onClick={async () => {
                              const res = await authFetch(`${API}/admin/custom-rates/${cr.rate_id}`, { method: "DELETE" });
                              if (res.ok) { toast.success("Custom rate removed"); fetchData(); }
                            }} data-testid={`delete-rate-${cr.rate_id}`}>
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {newRateOpen && (
                <div className="mt-4 soft-card !p-4 border-[var(--border-light)] space-y-3" style={{ borderColor: "var(--accent)", background: "var(--bg-secondary)" }}>
                  <h4 className="font-semibold" style={{ color: "var(--text-primary)" }}>Set Custom Rate</h4>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Client *</Label>
                      <select value={newRateForm.user_id} onChange={(e) => setNewRateForm({...newRateForm, user_id: e.target.value})}
                        className="w-full h-9 rounded-md border px-3 text-sm" style={{ borderColor: "var(--border-color)", background: "var(--bg-primary)", color: "var(--text-primary)" }}
                        data-testid="rate-client-select">
                        <option value="">Select Client</option>
                        {allClients.map((c) => <option key={c.user_id} value={c.user_id}>{c.name} ({c.email})</option>)}
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Service *</Label>
                      <select value={newRateForm.service_category} onChange={(e) => setNewRateForm({...newRateForm, service_category: e.target.value})}
                        className="w-full h-9 rounded-md border px-3 text-sm" style={{ borderColor: "var(--border-color)", background: "var(--bg-primary)", color: "var(--text-primary)" }}>
                        <option value="itr_filing">ITR Filing</option>
                        <option value="accounting">Accounting</option>
                        <option value="both">Combined Package</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Custom Price (INR) *</Label>
                      <Input type="number" value={newRateForm.custom_price} onChange={(e) => setNewRateForm({...newRateForm, custom_price: e.target.value})}
                        placeholder="e.g. 2000" className="rounded-md" data-testid="rate-price-input" />
                    </div>
                    <div>
                      <Label className="text-xs" style={{ color: "var(--text-secondary)" }}>Notes</Label>
                      <Input value={newRateForm.notes} onChange={(e) => setNewRateForm({...newRateForm, notes: e.target.value})}
                        placeholder="e.g. Loyal client discount" className="rounded-md" />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-[var(--accent)] text-white rounded-md" data-testid="save-rate-btn" onClick={async () => {
                      if (!newRateForm.user_id || !newRateForm.custom_price) { toast.error("Client and price are required"); return; }
                      const res = await authFetch(`${API}/admin/custom-rates`, { method: "POST", headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ ...newRateForm, custom_price: parseFloat(newRateForm.custom_price) }) });
                      if (res.ok) { toast.success("Custom rate set"); setNewRateOpen(false); setNewRateForm({ user_id: "", service_category: "itr_filing", custom_price: "", notes: "" }); fetchData(); }
                      else { const err = await res.json(); toast.error(err.detail || "Failed"); }
                    }}>Save</Button>
                    <Button size="sm" variant="ghost" onClick={() => setNewRateOpen(false)}>Cancel</Button>
                  </div>
                </div>
              )}
            </motion.div>
            )
          )}

          </div>{/* end flex-1 content */}
        </div>{/* end flex gap-3 */}
      </div>

      {/* Role Edit Dialog */}
      <Dialog open={roleDialogOpen} onOpenChange={setRoleDialogOpen}>
        <DialogContent className="rounded-lg" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']" style={{ color: "var(--text-primary)" }}>Edit User Role</DialogTitle>
          </DialogHeader>
          {selectedUserForRole && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 rounded-lg" style={{ background: "var(--bg-tertiary)" }}>
                {selectedUserForRole.picture ? (
                  <img src={selectedUserForRole.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--accent-bg)" }}>
                    <User className="w-5 h-5 text-[var(--accent)]" />
                  </div>
                )}
                <div>
                  <p className="font-medium" style={{ color: "var(--text-primary)" }}>{selectedUserForRole.name}</p>
                  <p className="text-sm" style={{ color: "var(--text-muted)" }}>{selectedUserForRole.email}</p>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium mb-1.5 block" style={{ color: "var(--text-primary)" }}>Select Role</label>
                <Select value={newRole} onValueChange={setNewRole}>
                  <SelectTrigger className="rounded-md" data-testid="role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="client">Client</SelectItem>
                    <SelectItem value="team_member">Team Member</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-3">
                <Button variant="outline" onClick={() => setRoleDialogOpen(false)} className="rounded-md">Cancel</Button>
                <Button onClick={handleSetRole} className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md" data-testid="save-role-btn">Save Changes</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createUserOpen} onOpenChange={setCreateUserOpen}>
        <DialogContent className="rounded-lg max-w-md" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display']" style={{ color: "var(--text-primary)" }}>Create New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Full Name *</label>
              <Input value={createForm.name} onChange={(e) => setCreateForm({ ...createForm, name: e.target.value })} placeholder="e.g. Priya Sharma" className="rounded-md" data-testid="create-user-name" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Email *</label>
              <Input type="email" value={createForm.email} onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })} placeholder="e.g. priya@example.com" className="rounded-md" data-testid="create-user-email" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Temporary Password *</label>
              <Input type="password" value={createForm.password} onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })} placeholder="Min 6 characters" className="rounded-md" data-testid="create-user-password" />
            </div>
            <div>
              <label className="text-sm font-medium mb-1 block" style={{ color: "var(--text-primary)" }}>Role *</label>
              <Select value={createForm.role} onValueChange={(v) => setCreateForm({ ...createForm, role: v })}>
                <SelectTrigger className="rounded-md" data-testid="create-user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client</SelectItem>
                  <SelectItem value="team_member">Team Member</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs" style={{ color: "var(--text-muted)" }}>
              The user can log in with this email and password. Share credentials securely.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setCreateUserOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={handleCreateUser} disabled={creating} className="bg-[var(--accent)] text-white hover:bg-[var(--accent-hover)] rounded-md" data-testid="confirm-create-user-btn">
                {creating ? "Creating..." : "Create User"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Thread Viewer Dialog */}
      <Dialog open={!!viewingThread} onOpenChange={(open) => { if (!open) { setViewingThread(null); setThreadMessages([]); } }}>
        <DialogContent className="rounded-lg max-w-lg max-h-[80vh] overflow-hidden flex flex-col" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] flex items-center gap-2" style={{ color: "var(--text-primary)" }}>
              <MessageSquare className="w-5 h-5 text-[var(--accent)]" />
              {viewingThread?.participant_1?.name} & {viewingThread?.participant_2?.name}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto p-4 rounded-lg border space-y-3 max-h-[50vh]" style={{ borderColor: "var(--border-color)", background: "var(--bg-tertiary)" }}>
            {threadMessages.map((msg) => {
              const senderName = msg.sender_id === viewingThread?.participant_1?.user_id
                ? viewingThread?.participant_1?.name
                : viewingThread?.participant_2?.name;
              return (
                <div key={msg.message_id} className="p-3 rounded-lg border" style={{ borderColor: "var(--border-color)", background: "var(--bg-secondary)" }}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-xs font-semibold" style={{ color: "var(--accent)" }}>{senderName}</span>
                    <span className="text-[10px]" style={{ color: "var(--text-muted)" }}>
                      {new Date(msg.created_at).toLocaleString("en-IN", { dateStyle: "short", timeStyle: "short" })}
                    </span>
                  </div>
                  <p className="text-sm" style={{ color: "var(--text-primary)" }}>{msg.content}</p>
                </div>
              );
            })}
            {threadMessages.length === 0 && (
              <p className="text-center py-6" style={{ color: "var(--text-muted)" }}>No messages in this thread</p>
            )}
          </div>
          <p className="text-xs text-center mt-2" style={{ color: "var(--text-muted)" }}>Read-only view</p>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <Dialog open={deleteUserOpen} onOpenChange={setDeleteUserOpen}>
        <DialogContent className="rounded-lg" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-red-400">Delete User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: "var(--text-secondary)" }}>
              Are you sure you want to permanently delete <strong style={{ color: "var(--text-primary)" }}>{deleteUserName}</strong>? This will remove all their profiles, documents, transactions, and messages.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteUserOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={handleDeleteUser} className="bg-red-500 text-white hover:bg-red-600 rounded-md" data-testid="confirm-delete-user">Delete Permanently</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Thread Confirmation */}
      <Dialog open={deleteThreadOpen} onOpenChange={setDeleteThreadOpen}>
        <DialogContent className="rounded-lg" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-red-400">Delete Thread</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: "var(--text-secondary)" }}>
              Are you sure you want to delete this message thread? All messages in this conversation will be permanently removed.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteThreadOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={handleDeleteThread} className="bg-red-500 text-white hover:bg-red-600 rounded-md" data-testid="confirm-delete-thread">Delete Thread</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Inquiry Confirmation */}
      <Dialog open={deleteInquiryOpen} onOpenChange={setDeleteInquiryOpen}>
        <DialogContent className="rounded-lg" style={{ background: "var(--bg-secondary)", borderColor: "var(--border-color)" }}>
          <DialogHeader>
            <DialogTitle className="font-['Playfair_Display'] text-red-400">Delete Inquiry</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p style={{ color: "var(--text-secondary)" }}>
              Are you sure you want to delete this inquiry? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={() => setDeleteInquiryOpen(false)} className="rounded-md">Cancel</Button>
              <Button onClick={handleDeleteInquiry} className="bg-red-500 text-white hover:bg-red-600 rounded-md" data-testid="confirm-delete-inquiry">Delete Inquiry</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    
    </PortalLayout>
  );
};

export default AdminDashboard;

